/**
 * portfolioStore — Plus multi-portfolio active-selection store.
 *
 * Round 92o++++++++N++++++++++ (May 2026).
 *
 * Subscribable global store (no Redux/Zustand) so any component on
 * the page can read the active portfolio selection AND mutate it
 * without prop-drilling. Persists across devices via the user doc
 * — `setActive()` POSTs `/api/portfolios/active` so the same choice
 * surfaces on every browser/phone the user signs in from.
 *
 * Shape:
 *   {
 *     loaded:  boolean,       // first hydrate complete?
 *     isPlus:  boolean,       // tier flag from /api/portfolios
 *     portfolios: [{...}],    // empty array for free users
 *     activeId: string|null,  // null = 'All Portfolios' view
 *     caps:    {...},         // free + plus limits payload
 *     allView: { ticker_count: number },
 *   }
 */

import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let state = {
  loaded: false,
  isPlus: false,
  portfolios: [],
  activeId: null,
  caps: null,
  allView: { ticker_count: 0 },
};

const listeners = new Set();

function emit() {
  // Versioned snapshot — keep a stable identity per change so
  // useSyncExternalStore doesn't fire on every emit unless something
  // actually moved.
  state = { ...state, _v: (state._v || 0) + 1 };
  for (const l of listeners) {
    try { l(); } catch { /* noop */ }
  }
}

export function getSnapshot() {
  return state;
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

let hydrated = false;
let hydratePromise = null;

/**
 * Hydrate the store from the backend. Safe to call from multiple
 * components — first call wins, subsequent calls share the in-flight
 * promise. Re-call manually after a portfolio is created / deleted
 * via `refresh()`.
 */
export async function hydrate() {
  if (hydrated) return state;
  if (hydratePromise) return hydratePromise;
  hydratePromise = (async () => {
    try {
      const [{ data: listData }, { data: activeData }] = await Promise.all([
        axios.get(`${API}/portfolios`, { withCredentials: true }),
        axios.get(`${API}/portfolios/active`, { withCredentials: true }),
      ]);
      state = {
        ...state,
        loaded: true,
        isPlus: !!listData?.is_plus,
        portfolios: Array.isArray(listData?.portfolios) ? listData.portfolios : [],
        activeId: activeData?.active_portfolio_id || null,
        caps: listData?.caps || null,
        allView: listData?.all_view || { ticker_count: 0 },
      };
      hydrated = true;
      emit();
    } catch {
      // Not signed in / endpoint missing — render as free tier so
      // legacy users see exactly today's UI.
      state = { ...state, loaded: true };
      emit();
    } finally {
      hydratePromise = null;
    }
    return state;
  })();
  return hydratePromise;
}

/** Force a re-fetch after a mutation (create / rename / delete). */
export async function refresh() {
  hydrated = false;
  return hydrate();
}

/** Persist the active selection. `null` = 'All Portfolios'. */
export async function setActive(portfolioId) {
  const next = portfolioId || null;
  // Optimistic update so the dropdown doesn't lag.
  state = { ...state, activeId: next };
  emit();
  try {
    await axios.post(
      `${API}/portfolios/active`,
      { active_portfolio_id: next },
      { withCredentials: true },
    );
  } catch {
    // Revert on failure so the UI doesn't lie about persisted state.
    await refresh();
  }
}

export async function createPortfolio({ name, broker, account_type }) {
  const { data } = await axios.post(
    `${API}/portfolios`,
    { name, broker, account_type },
    { withCredentials: true },
  );
  await refresh();
  return data?.portfolio || null;
}

export async function deletePortfolio(portfolioId) {
  // Round 92o++++++++N++++++++++ (Jun 2026) — cascade delete returns
  // counts so the caller can show an accurate toast.
  const { data } = await axios.delete(
    `${API}/portfolios/${portfolioId}`,
    { withCredentials: true },
  );
  await refresh();
  // Fire the global event so all dashboard widgets (Capital Tracker,
  // Daily Stock Tracker, Income by Portfolio, Heatmap, Calendar,
  // etc.) re-fetch with the now-zeroed numbers instead of waiting on
  // a manual page reload.
  try { window.dispatchEvent(new Event('portfolio-changed')); } catch { /* SSR */ }
  try { window.dispatchEvent(new Event('cash-changed')); } catch { /* SSR */ }
  return data || { ok: true };
}

export async function renamePortfolio(portfolioId, patch) {
  const { data } = await axios.patch(
    `${API}/portfolios/${portfolioId}`,
    patch,
    { withCredentials: true },
  );
  await refresh();
  return data?.portfolio || null;
}


// Round 92o++++++++N++++++++++ (Jun 2026) — auto-refresh on the
// global `portfolio-changed` event. CSV imports, lot
// adds/sells/deletes, cash deposits/withdrawals all fire this event;
// without an explicit listener here, the dropdown's ticker_count and
// the portfolios list stayed stale (user reported "test4 dropdown
// shows 0/500 tickers but I just imported 2 positions"). Listening
// at the store layer means every consumer subscribed via
// useActivePortfolioId() / usePortfolioScopeParams() gets the fresh
// data automatically.
if (typeof window !== 'undefined') {
  let pending = false;
  const onChange = () => {
    if (pending) return;
    pending = true;
    // Debounce so a burst of events (CSV importer fires multiple)
    // collapses into a single refresh.
    setTimeout(() => {
      pending = false;
      refresh().catch(() => {});
    }, 150);
  };
  window.addEventListener('portfolio-changed', onChange);
}
